import { createSelector } from '@reduxjs/toolkit';
import {
  ceil, isEmpty, max, reduce, round,
} from 'lodash';
import { ShowTotalCalculatorFactory } from '@sda/helpers/show-total-calculator.factory';
import { DEFAULT_CURRENCY } from '@sda/utils/lang';
import {
  stripeCurrentSKUSelector, stripeCurrentOrderSelector, stripeCurrentSKUAmountSelector, stripeIdempotencyKeySelector,
} from '..';
import { getCurrentShow, getProductsForShow, getProductsForShowTotalCapacity } from '../entities/shows';
import { getSessionUserFlat } from '../helpers/session';
import { selectItemByOptionalDictionary } from '../lib';
import type { PublicShowProfile, Show } from '@sda/interfaces/show';

/**
 * Return the most recently generated idempotency key for Stripe
 * @param  {any} stripe
 * @return {string}
 */
export const getIdempotencyKey = createSelector(
  stripeIdempotencyKeySelector,
  (key) => key,
);

export const getCurrentSKU = createSelector(
  stripeCurrentSKUSelector,
  (sku) => sku,
);

export const getCurrentSKUAmount = createSelector(
  stripeCurrentSKUAmountSelector,
  (amount) => amount,
);

export const getShowTotalCalculator = createSelector(
  stripeCurrentOrderSelector,
  stripeCurrentSKUSelector,
  stripeCurrentSKUAmountSelector,
  (state, show: Show) => show,
  (currentOrder, currentSKU, currentSKUAmount: number, show: Show) => ShowTotalCalculatorFactory
    .createCalculation({ show, stripe: { currentOrder, currentSKU, currentSKUAmount } }),
);

export const getCurrentShowMockCalculator = createSelector(
  getCurrentShow,
  getSessionUserFlat,
  getProductsForShowTotalCapacity,
  getProductsForShow,
  selectItemByOptionalDictionary,
  (show, user, totalCapacity, products, values: { publicProfiles: PublicShowProfile[] }) => {
    if (isEmpty(show)) {
      return null;
    }

    // For the sake of calculating the Stripe fee, assume that there will be 1.5 tickets per order
    const estimatedNumOrders = max([1, ceil(totalCapacity / 1.5)]);

    const price = reduce(products, (results, product) => ({ ...results, [product.id]: product.minimumPrice }), {});
    const quantity = reduce(products, (results, product) => ({ ...results, [product.id]: product.capacity > 0 ? product.capacity : 0 }), {});

    let taxIncluded = false;

    if (show.isOnlineOnly) {
      taxIncluded = user.taxIncluded;
    } else {
      taxIncluded = show.country !== 'US' && show.country !== 'CA';
    }

    const publicProfiles = values?.publicProfiles
      ? values.publicProfiles.map((profileData) => ({ ...profileData, profileSplitPercent: round(profileData.profileSplitPercent / 100, 2) }))
      : show.publicProfiles;

    const stripe = {
      currentSKU: {
        price,
        taxIncluded,
        showId: show.id,
        currency: show.currencyOverride || DEFAULT_CURRENCY,
        taxRate: show.isPending || !show.isIncomplete ? show.taxRate : user.taxRate,
      },
      currentSKUAmount: quantity,
      currentOrder: {
        numOrders: estimatedNumOrders,
      },
    };

    return ShowTotalCalculatorFactory.createCalculation({ stripe, show: { ...show, publicProfiles } });
  },
);
